import { ComponentProps } from 'preact';
import { ForwardedRef, forwardRef, useCallback } from 'preact/compat';

import { Dropdown } from '#UNSAFE_Dropdown';

import { MenuContainer } from './MenuContainer';
import { BaseMenuProps } from './Menu';
import { keyElemExtractor } from './menuUtils';
import { useMenuContext } from './useMenuContext';
import { dropdownMenuStyles } from './themes/DropdownMenuStyles.css';
import { MenuSkeleton } from './MenuSkeleton';
import { classNames } from '#utils/UNSAFE_classNames';
import { menuStyles } from './themes/MenuStyles.css';
import { UNSAFE_DOM_ACCESS, UnsafeDomHandle } from '#utils/UNSAFE_unsafeDomAccess';
type DropdownMenuProps = BaseMenuProps & DropdownMenuFloatingProps;

type DropdownProps = ComponentProps<typeof Dropdown>;
type DropdownMenuFloatingProps = {
  defaultPlacement: DropdownProps['placement'];
  offsetValue: DropdownProps['offset'];
  flipOptions: DropdownProps['flipOptions'];
  isMenuLoading?: boolean;
};

//TODO: Start consuming PRIVATE_Dropdown. JET-53371
/*Component used to render a dropdown menu. For now this is just rendered when device is not a phone. */
export const DropdownMenu = forwardRef(
  (
    {
      anchorRef,
      defaultPlacement,
      offsetValue,
      closeSubmenu,
      children,
      'aria-label': ariaLabel,
      currentFocus,
      menuId,
      menuLevel,
      flipOptions,
      isOpen,
      isMenuLoading = false,
      minWidth
    }: DropdownMenuProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const { onClose } = useMenuContext();

    const outsideMousedownHandler = useCallback<Required<DropdownProps>['onClose']>(
      (e) => {
        if (menuLevel === 'sub') return;
        // Outside click is not considered when menuItem was clicked
        if (e.reason === 'outsideMousedown') {
          const menuItemKey = keyElemExtractor(e.target as HTMLElement)?.key;
          if (!menuItemKey) {
            const isAnchorClick =
              (anchorRef.current as HTMLElement | null)?.contains?.(e.target) ||
              (anchorRef.current as UnsafeDomHandle)?.[UNSAFE_DOM_ACCESS]?.contains(e.target);
            onClose?.({ reason: isAnchorClick ? 'anchorClick' : 'outsideClick' });
          }
        }
      },
      [onClose, menuLevel]
    );

    // TODO - Reuse a dropdown component instead of creating floating by ourselves.

    /* 
    For this comment, we could divide menus in these categories:
    - Dropdown menus: "Attached" to an specific element.
    - Context menus: "Attached" to coords or an specific element.
    We are passing anchorRef as the logicalParent of the Layer used here, even if we pass coords. So we could say 
    that we are passing an incorrect logicalParentRef for context menus. But, passing coords has no impacts either on 
    accessibility or behavior since this logical parent is only used by legacy collections for dom searching to find 
    dropdown menus. More information about that here:  
    This comment also applies for sheet menus that in this specific context(relation between logicalParentRef
    and menu) can be considered "dropdown menus"
    For now using type assertion is safe enough, but it's kind of a dirty solution. This is a point that can be included 
    in near future conversation related to semantics of menus in favor of dividing dropdown and context menu: 
      
    */

    return (
      <Dropdown
        minWidth={minWidth ? Math.max(DEFAULT_MENU_MIN_WIDTH, minWidth) : DEFAULT_MENU_MIN_WIDTH}
        onClose={outsideMousedownHandler}
        isOpen={isOpen}
        placement={defaultPlacement}
        anchorRef={anchorRef}
        flipOptions={flipOptions}
        offset={offsetValue}
        shiftOptions={{
          mainAxis: true,
          crossAxis: true
        }}
        sizeOptions={{ isMaxWidthAdjusted: true, isMaxHeightAdjusted: true, maxWidthCeiling: 460 }}>
        <div
          class={classNames([dropdownMenuStyles.base, menuStyles.dropdownMenuContainerStyle])}
          ref={ref}>
          {isMenuLoading ? (
            <MenuSkeleton></MenuSkeleton>
          ) : (
            <MenuContainer
              menuLevel={menuLevel}
              closeSubmenu={closeSubmenu}
              aria-label={ariaLabel}
              currentFocus={currentFocus}
              menuId={menuId}
              display="dropdown"
              anchorRef={anchorRef}>
              {children}
            </MenuContainer>
          )}
        </div>
      </Dropdown>
    );
  }
);

const DEFAULT_MENU_MIN_WIDTH = 140;
